import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, RotateCcw, FileDown, FileText, RefreshCw, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { AutomationReportsService } from '@/services/modules/AutomationReportsService'
import { ProposalsService, type CommercialProposal } from '@/services/modules/ProposalsService'
import { useAuthority } from '@/hooks/useAuthority'

/** Intervalo entre as consultas ao andamento da geração (o PDF é assíncrono). */
const POLL_MS = 4000

const PIPELINE: Record<string, { label: string; variant: 'blue' | 'green' | 'red' }> = {
  EM_NEGOCIACAO: { label: 'Em negociação', variant: 'blue' },
  GANHA:         { label: 'Ganha',         variant: 'green' },
  PERDIDA:       { label: 'Perdida',       variant: 'red' },
}

/** Status por extenso (exportação). */
const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  GERADA:   'Gerada',
}

export function ProposalsListPage() {
  const navigate = useNavigate()
  const { can } = useAuthority()
  const canCreate = can('commercial.proposals.create')
  const canEdit = can('commercial.proposals.edit')
  const canDelete = can('commercial.proposals.delete')
  const canGenerate = can('commercial.proposals.generate')
  const canReopen = can('commercial.proposals.close_board')
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({
    searchPlaceholder: 'Buscar por empresa',
    initialStatus: '',
    statusOptions: [
      { value: '', label: 'Todas' },
      { value: 'RASCUNHO', label: 'Rascunho' },
      { value: 'GERADA', label: 'Gerada' },
    ],
  })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(ProposalsService, { size: 25, reloadKey: reload, filters: sf.filters })
  const exp = useDataTableExport({
    filename: 'propostas',
    service: ProposalsService,
    filters: sf.filters,
    columns: [
      { header: 'Empresa',        accessor: (p: CommercialProposal) => p.companyName },
      { header: 'A/C',            accessor: (p: CommercialProposal) => p.recipientName ?? '' },
      { header: 'Vidas',          accessor: (p: CommercialProposal) => p.livesCount },
      { header: 'Categoria',      accessor: (p: CommercialProposal) => p.category?.name ?? '' },
      { header: 'Assinaturas',    accessor: (p: CommercialProposal) => (p.tiers ?? []).map(t => t.name).join(', ') },
      { header: 'Status',         accessor: (p: CommercialProposal) => STATUS_LABELS[p.status] ?? p.status },
      { header: 'Situação',       accessor: (p: CommercialProposal) => (PIPELINE[p.pipelineStatus] ?? PIPELINE.EM_NEGOCIACAO).label },
      { header: 'PDF gerado em',  accessor: (p: CommercialProposal) => p.lastPdf?.url ? (p.lastPdf.formatted?.generatedAt ?? '') : 'Não gerada' },
      { header: 'Criada em',      accessor: (p: CommercialProposal) => p.formatted?.createdAt ?? '' },
    ],
  })
  const [deleting, setDeleting] = useState<CommercialProposal | null>(null)
  /** Proposta cujo PDF está sendo gerado agora (uma por vez, como na tela da proposta). */
  const [generating, setGenerating] = useState<string | null>(null)
  const [regenerating, setRegenerating] = useState<CommercialProposal | null>(null)
  const timer = useRef<number | undefined>(undefined)

  // Sair da tela no meio da geração não pode deixar a consulta rodando.
  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current) }, [])

  /**
   * Gera o PDF e acompanha até concluir. Ao terminar, a lista recarrega: é de
   * lá que vem o link para visualizar a proposta pronta.
   */
  async function handleGenerate(p: CommercialProposal) {
    setGenerating(p.id)
    try {
      const report = await ProposalsService.generate(p.id)
      toast.success({ title: 'Geração iniciada', description: `A proposta de ${p.companyName} está sendo montada.` })

      if (timer.current) window.clearInterval(timer.current)
      timer.current = window.setInterval(async () => {
        try {
          const fresh = await AutomationReportsService.show(report.id)
          if (fresh.status !== 'CONCLUIDO' && fresh.status !== 'FALHOU') return

          window.clearInterval(timer.current); timer.current = undefined
          setGenerating(null)
          if (fresh.status === 'CONCLUIDO') {
            toast.success({ title: 'Proposta gerada', description: 'Use "Visualizar proposta" para abrir o PDF.' })
            setReload(v => v + 1)
          } else {
            toast.error({ title: 'Falha ao gerar', description: fresh.errorMessage ?? 'Tente novamente.' })
          }
        } catch { /* tenta de novo no próximo ciclo */ }
      }, POLL_MS) as unknown as number
    } catch (e) {
      setGenerating(null)
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    }
  }

  function openPdf(p: CommercialProposal) {
    if (!p.lastPdf?.url) return
    window.open(p.lastPdf.url, '_blank', 'noopener,noreferrer')
  }

  async function handleDelete() {
    if (!deleting) return
    try { await ProposalsService.destroy(deleting.id); toast.success('Proposta removida.'); setDeleting(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  async function handleReopen(p: CommercialProposal) {
    try { await ProposalsService.reopen(p.id); toast.success('Proposta reaberta — voltou ao kanban.'); setReload(v => v + 1) }
    catch { toast.error('Erro ao reabrir.') }
  }

  const columns: DataTableColumn<CommercialProposal>[] = [
    { key: 'company', header: 'Empresa', alwaysVisible: true, render: p => (
      <div>
        <div className="font-medium text-gray-800">{p.companyName}</div>
        {p.recipientName && <div className="text-[11px] text-gray-500">A/C: {p.recipientName}</div>}
      </div>
    ) },
    { key: 'lives', header: 'Vidas', align: 'center', className: 'w-24', render: p => p.livesCount },
    { key: 'category', header: 'Categoria', align: 'center', className: 'w-32',
      render: p => p.category ? <Badge variant="blue">{p.category.name}</Badge> : <span className="text-xs text-gray-400">—</span> },
    { key: 'tiers', header: 'Assinaturas', align: 'center', className: 'w-28', render: p => <Badge variant="gray">{p.tiers?.length ?? 0}</Badge> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-28', render: p => <StatusBadge status={p.status} /> },
    { key: 'pipeline', header: 'Situação', align: 'center', className: 'w-36', render: p => {
      const it = PIPELINE[p.pipelineStatus] ?? PIPELINE.EM_NEGOCIACAO
      return <Badge variant={it.variant}>{it.label}</Badge>
    } },
    { key: 'pdf', header: 'PDF', align: 'center', className: 'w-44', render: p => (
      generating === p.id
        ? <span className="inline-flex items-center gap-1 text-[11px] text-gray-500"><Loader2 size={12} className="animate-spin" /> gerando</span>
        : p.lastPdf?.url
          ? <button type="button" onClick={() => openPdf(p)}
              className="cursor-pointer inline-flex items-center gap-1 text-[11px] text-brand-600 underline-offset-2 hover:underline">
              <FileText size={12} /> {p.lastPdf.formatted?.generatedAt ?? 'abrir'}
            </button>
          : <span className="text-[11px] text-gray-400">não gerada</span>
    ) },
    { key: 'createdAt', header: 'Criada em', className: 'w-40', render: p => p.formatted?.createdAt ?? '—' },
  ]

  return (
    <>
      <PageHeader
        title="Propostas comerciais"
        subtitle="Monte e exporte propostas em PDF"
        crumbs={[{ label: 'Comercial' }, { label: 'Propostas' }]}
        actions={canCreate ? <Button onClick={() => navigate('/commercial/proposals/novo')} iconLeft={<Plus size={14} />}>Nova proposta</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="commercial.proposals"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns} rows={rows} loading={loading} getRowKey={p => p.id}
        emptyMessage="Nenhuma proposta cadastrada."
        rowActions={(p) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/commercial/proposals/${p.id}/editar`) }] : []),
          ...(p.lastPdf?.url
            ? [
                { key: 'view', label: 'Visualizar proposta', icon: <FileText size={14} />, onClick: () => openPdf(p) },
                ...(canGenerate ? [{ key: 'regenerate', label: 'Gerar de novo', icon: <RefreshCw size={14} />, onClick: () => setRegenerating(p) }] : []),
              ]
            : canGenerate ? [{ key: 'generate', label: 'Gerar proposta', icon: <FileDown size={14} />, onClick: () => void handleGenerate(p) }] : []),
          ...(canReopen && p.pipelineStatus && p.pipelineStatus !== 'EM_NEGOCIACAO'
            ? [{ key: 'reopen', label: 'Reabrir (voltar ao kanban)', icon: <RotateCcw size={14} />, onClick: () => handleReopen(p) }]
            : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setDeleting(p) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!regenerating}
        onClose={() => setRegenerating(null)}
        onConfirm={() => { const p = regenerating; setRegenerating(null); if (p) void handleGenerate(p) }}
        title="Gerar a proposta de novo?"
        description={`A proposta de "${regenerating?.companyName}" será montada novamente com os dados e valores de agora. O PDF anterior deixa de ser o atual da lista.`}
        confirmLabel="Gerar de novo"
      />

      <ConfirmDialog
        open={!!deleting} onClose={() => setDeleting(null)} onConfirm={handleDelete}
        title="Remover proposta?" description={`Remover a proposta de "${deleting?.companyName}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover" variant="danger"
      />
      {sf.drawer}
    </>
  )
}
